import { Check } from "lucide-react";
import { cn } from "@/lib/utils";
import { Btn } from "@/components/primitives";
import { track } from "@/lib/analytics";

// Single plan card. Used on Home preview and Plans page. Content comes from
// the central config (content/site.js) — never duplicated in components.
export default function PlanCard({ plan, showAll = true }) {
  const recommended = plan.recommended;
  return (
    <div
      data-testid={`plan-card-${plan.id}`}
      className={cn(
        "relative flex flex-col rounded-[2rem] p-7 md:p-8 border transition-all duration-300",
        recommended
          ? "bg-forest text-white border-forest lg:scale-[1.03] shadow-[0_24px_60px_rgba(3,34,26,0.25)]"
          : "bg-white text-dark-neutral border-border-neutral hover:border-mint hover:-translate-y-1 shadow-[0_10px_40px_rgba(3,34,26,0.05)]"
      )}
    >
      {recommended && (
        <span className="absolute -top-3 left-8 inline-flex items-center rounded-full bg-lime px-4 py-1 text-xs font-extrabold uppercase tracking-wider text-forest">
          Recommended
        </span>
      )}
      <div>
        <p
          className={cn(
            "text-xs font-bold uppercase tracking-[0.18em]",
            recommended ? "text-lime" : "text-mint"
          )}
        >
          {plan.name}
        </p>
        <h3
          className={cn(
            "mt-2 text-2xl font-extrabold",
            recommended ? "text-white" : "text-forest"
          )}
        >
          {plan.tagline}
        </h3>
        <p
          className={cn(
            "mt-3 text-sm leading-relaxed",
            recommended ? "text-white/75" : "text-mid-neutral"
          )}
        >
          {plan.description}
        </p>
      </div>

      <ul className="mt-6 space-y-3 flex-1">
        {(showAll ? plan.inclusions : plan.inclusions.slice(0, 6)).map((inc) => (
          <li key={inc} className="flex items-start gap-3 text-sm">
            <span
              className={cn(
                "mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full",
                recommended ? "bg-lime text-forest" : "bg-soft text-mint"
              )}
            >
              <Check className="h-3.5 w-3.5" strokeWidth={3} />
            </span>
            <span className={recommended ? "text-white/85" : "text-dark-neutral"}>
              {inc}
            </span>
          </li>
        ))}
      </ul>

      {plan.notes?.length > 0 && (
        <div
          className={cn(
            "mt-5 space-y-2 rounded-2xl p-4 text-xs leading-relaxed",
            recommended ? "bg-white/10 text-white/70" : "bg-soft text-mid-neutral"
          )}
        >
          {plan.notes.map((n, i) => (
            <p key={i}>{n}</p>
          ))}
        </div>
      )}

      <div className="mt-7">
        <Btn
          to="/plans"
          variant={recommended ? "primary" : "secondary"}
          className="w-full"
          withArrow
          onClick={() =>
            track("plan_cta_clicked", { plan: plan.id })
          }
          data-testid={`plan-cta-${plan.id}`}
        >
          {plan.ctaLabel}
        </Btn>
      </div>
    </div>
  );
}
